import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../../lib/api";

const STATUT = {
  brouillon: ["Brouillon", "bg-zinc-700/50 text-zinc-300"],
  a_valider: ["À valider (responsable technique)", "bg-amber-500/15 text-amber-300 border border-amber-500/40"],
  valide: ["Validée", "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"],
  retire: ["Retirée", "bg-red-500/15 text-red-300"],
};
const empty = { epreuve: "autodefense", numero: 1, titre: "", situation: "", reponse: "", etapes: [], exclusions: "", image: "", notes: "" };
const inp = "w-full rounded-lg bg-black border border-zinc-800 p-2 text-sm";
const ghostBtn = "text-xs rounded-lg border border-zinc-700 px-2.5 py-1.5 text-zinc-300 hover:border-amber-500 hover:text-amber-300";

async function pdf(url) {
  try {
    const r = await api.get(url, { responseType: "blob" });
    window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank", "noopener");
  } catch { toast.error("PDF impossible"); }
}

function SeqCard({ s, onEdit, onStatut, onDelete }) {
  const [label, cls] = STATUT[s.statut] || STATUT.brouillon;
  return (
    <div className="rounded-xl border border-zinc-800 bg-black/30 overflow-hidden" data-testid={`pr-seq-${s.id}`}>
      {s.image && <img src={s.image} alt={s.titre} className="w-full aspect-[3/2] object-cover" />}
      <div className="p-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <b>{s.numero}. {s.titre}</b>
          <span className={`text-[10px] rounded-full px-2 py-0.5 ${cls}`} data-testid={`pr-seq-statut-${s.id}`}>{label}</span>
          {s.statut === "valide" && <span className="text-[10px] text-zinc-500">par {s.valide_par} · {s.valide_at}</span>}
        </div>
        <p className="text-xs text-zinc-400"><span className="text-zinc-500">Situation :</span> {s.situation}</p>
        <p className="text-xs text-zinc-300"><span className="text-zinc-500">Réponse attendue :</span> {s.reponse}</p>
        {s.etapes?.length > 0 && <ol className="list-decimal pl-5 text-xs text-zinc-400 space-y-0.5">{s.etapes.map((e, i) => <li key={i}>{e}</li>)}</ol>}
        {s.exclusions && <p className="text-[11px] text-red-300/80">⛔ {s.exclusions}</p>}
        <div className="flex flex-wrap gap-2 pt-1">
          <button onClick={() => onEdit(s)} className="text-xs text-amber-300" data-testid={`pr-seq-edit-${s.id}`}>✏️ Modifier</button>
          {s.statut !== "valide" && s.statut !== "retire" && <button onClick={() => onStatut(s, "a_valider")} className="text-xs text-amber-300" data-testid={`pr-seq-avalider-${s.id}`}>→ Soumettre à validation</button>}
          {s.statut !== "valide" && <button onClick={() => onStatut(s, "valide")} className="text-xs text-emerald-300" data-testid={`pr-seq-valider-${s.id}`}>✓ Valider (resp. technique)</button>}
          {s.statut === "valide" && <button onClick={() => onStatut(s, "retire")} className="text-xs text-red-300" data-testid={`pr-seq-retirer-${s.id}`}>Retirer</button>}
          {s.statut !== "valide" && <button onClick={() => onDelete(s)} className="text-xs text-red-300 ml-auto" data-testid={`pr-seq-del-${s.id}`}>🗑️</button>}
        </div>
      </div>
    </div>
  );
}

export function PetiteReleveCatalogue() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [cat, setCat] = useState("A");
  const load = useCallback(async () => {
    try { const r = await api.get("/jm/petite-releve/sequences"); setData(r.data); } catch { toast.error("Erreur catalogue Petite relève"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    try {
      const body = { ...form, numero: Number(form.numero) || 1, etapes: (form.etapes || []).filter(Boolean) };
      if (form.id) await api.patch(`/jm/petite-releve/sequences/${form.id}`, body); else await api.post("/jm/petite-releve/sequences", body);
      toast.success("Séquence enregistrée"); setForm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const statut = async (s, st) => {
    let par = "";
    if (st === "valide") { par = window.prompt("Nom du responsable technique qui valide cette séquence :", ""); if (!par) return; }
    try { await api.post(`/jm/petite-releve/sequences/${s.id}/statut`, { statut: st, valide_par: par }); toast.success(st === "valide" ? `Validée par ${par}` : "Statut mis à jour"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const del = async (s) => { if (!window.confirm(`Supprimer « ${s.titre} » ?`)) return; try { await api.delete(`/jm/petite-releve/sequences/${s.id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } };
  if (!data) return null;
  const { sequences, epreuves, resume } = data;
  return (
    <section className="mt-8 space-y-4" data-testid="pr-catalogue">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-semibold text-sm">🐣 Petite relève — catalogue des séquences autorisées (étape 1)</h3>
          <p className="text-xs text-zinc-500">Chaque séquence doit être approuvée et illustrée par le responsable technique avant son utilisation (art. 11 et 14 du règlement pilote). Une modification du contenu annule la validation.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={cat} onChange={(e) => setCat(e.target.value)} className="text-xs rounded-lg bg-black border border-zinc-800 px-2 py-1.5" data-testid="pr-cat-select">
            {["A", "B", "C", "D"].map((k) => <option key={k} value={k}>Catégorie {k} — {2 + "ABCD".indexOf(k) + 1} ans</option>)}
          </select>
          <button onClick={() => pdf(`/jm/petite-releve/feuille-juges.pdf?epreuve=autodefense&categorie=${cat}`)} className={ghostBtn} data-testid="pr-pdf-juges-ad">🖨️ Feuille juges /20 · Autodéfense {cat}</button>
          <button onClick={() => pdf(`/jm/petite-releve/feuille-juges.pdf?epreuve=bjj_technique&categorie=${cat}`)} className={ghostBtn} data-testid="pr-pdf-juges-bjj">🖨️ Feuille juges /20 · BJJ {cat}</button>
          <button onClick={() => pdf("/jm/petite-releve/feuilles-juges-toutes.pdf")} className={ghostBtn} data-testid="pr-pdf-juges-toutes">📚 Toutes catégories (A–D)</button>
          <button onClick={() => pdf("/jm/petite-releve/fiche-sequences.pdf")} className={ghostBtn} data-testid="pr-pdf-fiche" title="Seulement les séquences validées">📄 Fiche séquences validées</button>
          <button onClick={() => pdf("/jm/petite-releve/fiche-sequences.pdf?toutes=true")} className={ghostBtn} data-testid="pr-pdf-fiche-toutes">📄 Fiche brouillon (toutes)</button>
          <button onClick={() => setForm({ ...empty })} className="text-xs bg-amber-500 text-black font-semibold rounded-lg px-3 py-1.5" data-testid="pr-seq-new">+ Séquence</button>
        </div>
      </div>
      {form && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 space-y-2" data-testid="pr-seq-form">
          <div className="grid sm:grid-cols-4 gap-2">
            <select value={form.epreuve} onChange={(e) => set("epreuve", e.target.value)} className={inp} data-testid="pr-seq-epreuve">{Object.entries(epreuves).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
            <input type="number" value={form.numero} onChange={(e) => set("numero", e.target.value)} className={inp} placeholder="N°" />
            <input value={form.titre} onChange={(e) => set("titre", e.target.value)} className={`${inp} sm:col-span-2`} placeholder="Titre *" data-testid="pr-seq-titre" />
          </div>
          <textarea value={form.situation} onChange={(e) => set("situation", e.target.value)} className={inp} rows={2} placeholder="Situation standardisée" />
          <textarea value={form.reponse} onChange={(e) => set("reponse", e.target.value)} className={inp} rows={2} placeholder="Réponse attendue" />
          <textarea value={(form.etapes || []).join("\n")} onChange={(e) => set("etapes", e.target.value.split("\n"))} className={inp} rows={4} placeholder="Étapes observables (une par ligne)" />
          <input value={form.exclusions} onChange={(e) => set("exclusions", e.target.value)} className={inp} placeholder="Rappel des exclusions" />
          <input value={form.image} onChange={(e) => set("image", e.target.value)} className={inp} placeholder="Illustration (URL ou /brand/petite-releve/…)" />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setForm(null)} className="text-xs border border-zinc-700 rounded-lg px-3 py-1.5">Annuler</button>
            <button onClick={save} disabled={!form.titre} className="text-xs bg-amber-500 text-black font-semibold rounded-lg px-3 py-1.5 disabled:opacity-50" data-testid="pr-seq-save">Enregistrer</button>
          </div>
        </div>
      )}
      {Object.entries(epreuves).map(([k, label]) => (
        <div key={k} className="space-y-2" data-testid={`pr-epreuve-${k}`}>
          <div className="text-xs uppercase tracking-wider text-amber-400">{label} <span className="text-zinc-500 normal-case">· {resume[k].valides}/{resume[k].total} validée(s)</span></div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
            {sequences.filter((s) => s.epreuve === k).map((s) => <SeqCard key={s.id} s={s} onEdit={setForm} onStatut={statut} onDelete={del} />)}
          </div>
        </div>
      ))}
    </section>
  );
}
